import { GuillochePattern } from "@shared/components/Brand/GuillochePattern";
import React from "react";

import { AppFootnote } from "@/components/AppFootnote";

import { HeroSection } from "./HeroSection";

interface USDvLandingPanelProps {
    /**
     * Optional override for the hero content. Defaults to the standard
     * `<HeroSection />` (USDv coin + "Get USDv and earn X% APY" + "$X in circulation")
     * shown on both the disconnected landing and the connected-no-
     * yaas state. Pass a different element (e.g. `<PortfolioSummary />`
     * showing live distributions) for the active-position state where
     * the same panel chrome wraps a different focal piece.
     */
    hero?: React.ReactNode;
    showFootnote?: boolean;
}

/**
 * The desktop 60% landing panel — guilloche pattern background, a
 * centered hero (defaults to `HeroSection`), and the YaaS distribution-
 * rules footnote anchored at the bottom.
 *
 * Single source of truth for the left side of every USDv landing-style
 * surface — the disconnected landing, the connected-no-yaas state, and
 * the active-position state all wrap this same panel. Per-state content
 * differences live in the `hero` prop; the panel chrome (guilloche,
 * footnote, layout) stays consistent.
 *
 * Mobile rendering is handled at the page level (each consumer renders
 * its own mobile hero/footnote inline) since the surrounding chrome
 * differs by page (BuyWidget vs action stack vs etc.).
 */
export function USDvLandingPanel({
    hero,
    showFootnote = true
}: USDvLandingPanelProps = {}): React.ReactElement {
    return (
        <div className="hidden md:flex md:w-[60%] flex-col items-center relative overflow-hidden">
            {/* Guilloche pattern — absolute behind, full panel. */}
            <GuillochePattern src="/img/guilloche-1.svg" animateIn />

            {/* Hero — centered in remaining space. `pt-25` (100px)
                adds a top inset so the hero sits below the vertical
                center, giving the panel room to breathe at the top.
                No horizontal padding — the inner `max-w-lg` clamps
                the hero width directly. */}
            <div className="flex-1 min-h-0 w-full flex items-center justify-center relative z-10 pt-25">
                <div className="w-full max-w-lg">{hero ?? <HeroSection />}</div>
            </div>

            {/* Footnote — in flow at the bottom of the column. `mt-4`
                guarantees a min 16px breathing room below the hero
                (matches the title→circulation gap inside HeroSection) for
                short viewports where the centered flex-1 area
                collapses to its minimum and would otherwise butt the
                footnote up against the circulation row. */}
            <div
                aria-hidden={!showFootnote}
                className={`relative z-10 mt-4 pb-24 w-full max-w-md ${showFootnote ? "visible" : "invisible"}`}
            >
                <AppFootnote />
            </div>
        </div>
    );
}
